/**
 * 「渠道余额查询」页的请求组装与结果解析（纯函数，便于单测）。
 *
 * 链路：前端 testApi.executeTest({ testType: 'BALANCE_QUERY', ... })
 *   → test-platform FiatPaymentClient（签名后 POST /v1/fiat/payment/channel/balance/query）
 *   → fiat-payment ChannelBalanceQueryService → 各渠道 queryChannelBalance。
 * 余额查询对所有实现了 queryChannelBalance 的渠道通用，这里不写任何渠道特例。
 *
 * 响应结构依据 fiat-payment ChannelBalanceResponse：
 *   data 为数组，每项 { channelAccountNo, channelCode, currency, availableBalance, balanceDetails?[{ type, amount }] }。
 *   FINCRA：availableBalance = Fincra availableBalance，balanceDetails[type=LEDGER] = ledgerBalance（账面余额）。
 */

const trimToUndefined = (value) => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * 组装余额查询请求体。
 *
 * - currency 不选就不传：中台会按 fiat_channel_config 里该渠道开放的币种逐个查询后聚合（不传 ≠ 传空串）。
 * - channelAccountNo 选填：同渠道同币种有多个渠道账户时用来指定。
 * - channelCode 一并带上便于在执行记录里看清请求；test-platform 转发前仍会用已校验的外层 channelCode 覆盖它。
 * - 不放 requestId：test-platform 会统一补；也不放 targetPartner / businessPartner（test-platform 会剥离，见其注释）。
 *
 * @param {{channelCode?: string, currency?: string, channelAccountNo?: string}} values 表单值
 * @returns {object} 请求体对象（调用方再 JSON.stringify）
 */
export const buildBalanceRequestBody = (values = {}) => {
  const body = {};
  const channelCode = trimToUndefined(values.channelCode);
  const currency = trimToUndefined(values.currency);
  const channelAccountNo = trimToUndefined(values.channelAccountNo);
  if (channelCode) body.channelCode = channelCode;
  if (currency) body.currency = currency.toUpperCase();
  if (channelAccountNo) body.channelAccountNo = channelAccountNo;
  return body;
};

/** responseBody 可能是字符串（test-platform 原样回传）也可能已被解析成对象 */
const parseResponseBody = (responseBody) => {
  if (responseBody == null) return null;
  if (typeof responseBody !== 'string') return responseBody;
  try {
    return JSON.parse(responseBody);
  } catch (e) {
    return null;
  }
};

/**
 * 解析 executeTest 的返回。
 *
 * 三层结果要分开看：
 *   1. test-platform 外层 code：403 = 权限校验没过；500 = 转发失败（data.errorMsg 里是原因，如中台 4xx 的报文）；
 *   2. 中台 ResponseResult.code：非 200 = 中台拒绝（msg 里是原因，如 channelCode 非法）；
 *   3. data 数组为空 = 查询成功但没有余额（没配渠道账户 / 渠道调用失败 / 钱包里没有该币种）——
 *      中台对这几种情况都返回空列表、不报错，「查不到」与「余额为 0」的区别是有没有这一行，而不是金额是不是 0。
 *
 * @param {object} result axios 响应的 data（test-platform 的执行结果）
 * @returns {{ok: boolean, rows: Array<object>, message: string|null}}
 *          ok=true 时 rows 可能为空数组（表示「查不到」）；ok=false 时 message 为失败原因（可能为 null）
 */
export const parseBalanceResult = (result) => {
  if (!result || String(result.code) !== '200') {
    return { ok: false, rows: [], message: result?.msg || result?.data?.errorMsg || null };
  }
  const body = parseResponseBody(result.data?.responseBody);
  if (!body) {
    return { ok: false, rows: [], message: result.data?.errorMsg || null };
  }
  if (String(body.code) !== '200') {
    return { ok: false, rows: [], message: body.msg || null };
  }
  const list = Array.isArray(body.data) ? body.data : [];
  const rows = list.filter(item => item && typeof item === 'object').map((item, index) => ({
    // 同一渠道同一币种可能有多个渠道账户，key 带上下标避免重复
    key: `${item.channelCode || ''}|${item.currency || ''}|${item.channelAccountNo || ''}|${index}`,
    channelCode: item.channelCode,
    currency: item.currency,
    availableBalance: item.availableBalance,
    channelAccountNo: item.channelAccountNo,
    balanceDetails: Array.isArray(item.balanceDetails) ? item.balanceDetails.filter(Boolean) : [],
  }));
  return { ok: true, rows, message: null };
};
